import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserManagement } from '../../hooks/useUserManagement';
import {
  HiArrowPath,
  HiMagnifyingGlass,
  HiExclamationCircle,
  HiUsers,
  HiPlus,
  HiTrash,
  HiCheckCircle,
  HiXMark
} from 'react-icons/hi2';
import {
  getSuggestedAthletes,
  addSuggestedAthlete,
  removeSuggestedAthlete,
  SuggestedAthleteWithData
} from '../../services/suggestedAthletesService';
import { searchUsernames, UsernameData } from '../../services/connections';
import { getUserPublicData } from '../../services/userService';
import { trainingTypeService } from '../../services/trainingTypeService';

const SuggestedAthletesManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const { users, loading: usersLoading, refreshData } = useUserManagement();
  
  const [suggestedAthletes, setSuggestedAthletes] = useState<SuggestedAthleteWithData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<UsernameData & { publicData?: any }>>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sportsMap, setSportsMap] = useState<Record<string, string>>({});

  useEffect(() => {
    trainingTypeService
      .fetchSports()
      .then((allSports) => {
        const map = allSports.reduce((acc: Record<string, string>, sport) => {
          if (sport.id) acc[sport.id] = sport.name;
          return acc;
        }, {});
        setSportsMap(map);
      })
      .catch((err) => console.error('Error fetching sports:', err));
  }, []);

  // Resolve display sport names from a user's trainingSportIDs
  const getSportNames = (trainingSportIDs?: string[]): string =>
    (trainingSportIDs || [])
      .map((id) => sportsMap[id])
      .filter(Boolean)
      .join(', ');

  // Filter out users who are already suggested athletes
  const suggestedAthleteIds = suggestedAthletes.map(athlete => athlete.userId);
  
  // For browse mode (when not searching), show first 20 users
  const availableUsers = users.filter(user => !suggestedAthleteIds.includes(user.id)).slice(0, 20);
  
  // For search results, filter out already suggested athletes
  const availableSearchResults = searchResults.filter(user => !suggestedAthleteIds.includes(user.userId));

  const loadSuggestedAthletes = async () => {
    try {
      setLoading(true);
      setError(null);
      const athletes = await getSuggestedAthletes();
      setSuggestedAthletes(athletes);
    } catch (err) {
      console.error('Error loading suggested athletes:', err);
      setError('Failed to load suggested athletes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestedAthletes();
  }, []);

  const handleAddAthlete = async (userId: string) => {
    if (!currentUser) return;

    setAddingUserId(userId);
    setError(null);
    setSuccessMessage(null);

    try {
      await addSuggestedAthlete(userId, currentUser.uid);
      await loadSuggestedAthletes();
      setSuccessMessage('Athlete added to suggested list');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error adding athlete:', err);
      setError('Failed to add athlete');
    } finally {
      setAddingUserId(null);
    }
  };

  const handleRemoveAthlete = async (userId: string) => {
    setRemovingUserId(userId);
    setError(null);
    setSuccessMessage(null);

    try {
      await removeSuggestedAthlete(userId);
      await loadSuggestedAthletes();
      setSuccessMessage('Athlete removed from suggested list');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error removing athlete:', err);
      setError('Failed to remove athlete');
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([loadSuggestedAthletes(), refreshData()]);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      const results = await searchUsernames(searchTerm.trim(), 50);
      
      // Fetch public data for each result
      const resultsWithData = await Promise.all(
        results.map(async (usernameData) => {
          const publicData = await getUserPublicData(usernameData.userId);
          return {
            ...usernameData,
            publicData
          };
        })
      );
      
      setSearchResults(resultsWithData);
    } catch (err) {
      console.error('Error searching usernames:', err);
      setError('Failed to search usernames');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
    setError(null);
  };

  if (loading && suggestedAthletes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-base-content/70">Loading suggested athletes...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-3xl font-semibold font-grotesk">Suggested Athletes Management</h1>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || usersLoading}
          className="btn btn-primary btn-sm"
        >
          <HiArrowPath className={`w-4 h-4 mr-2 ${(loading || usersLoading) ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <HiExclamationCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success mb-6">
          <HiCheckCircle className="w-5 h-5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Current Suggested Athletes */}
      <div className="card bg-base-100 shadow-sm border border-base-200 mb-8">
        <div className="card-body">
          <h2 className="text-xl font-semibold mb-4 font-grotesk">
            Current Suggested Athletes ({suggestedAthletes.length})
          </h2>
          
          {suggestedAthletes.length === 0 ? (
            <div className="text-center py-8 text-base-content/60">
              No suggested athletes added yet. Search and add athletes below.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr className="bg-base-200">
                    <th className="font-semibold">Athlete</th>
                    <th className="font-semibold">User ID</th>
                    <th className="font-semibold">Sport</th>
                    <th className="font-semibold">Added At</th>
                    <th className="font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestedAthletes.map((athlete) => (
                    <tr key={athlete.userId} className="hover:bg-base-200">
                      <td>
                        <div className="flex items-center space-x-3">
                          {athlete.publicData?.imageUrlTiny || athlete.publicData?.imageUrl ? (
                            <img
                              src={athlete.publicData.imageUrlTiny || athlete.publicData.imageUrl}
                              alt={athlete.publicData.username || 'Athlete'}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                              {athlete.publicData?.username?.charAt(0).toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {athlete.publicData?.username || 'Unknown'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="text-xs bg-base-200 px-2 py-1 rounded">
                          {athlete.userId.slice(0, 8)}...
                        </code>
                      </td>
                      <td>
                        {getSportNames(athlete.publicData?.trainingSportIDs) ||
                          'N/A'}
                      </td>
                      <td className="text-sm">
                        {new Intl.DateTimeFormat('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }).format(athlete.addedAt)}
                      </td>
                      <td>
                        <button
                          onClick={() => handleRemoveAthlete(athlete.userId)}
                          disabled={removingUserId === athlete.userId}
                          className="btn btn-sm btn-ghost text-error hover:bg-error hover:text-white"
                          title="Remove from suggested athletes"
                        >
                          {removingUserId === athlete.userId ? (
                            <span className="loading loading-spinner loading-sm"></span>
                          ) : (
                            <HiTrash className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add New Suggested Athletes */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body">
          <h2 className="text-xl font-semibold mb-4 font-grotesk">Add Suggested Athletes</h2>
          
          {/* Search Bar */}
          <div className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !isSearching && handleSearch()}
                  className="input input-bordered pl-10 w-full"
                  disabled={isSearching}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching || !searchTerm.trim()}
                className="btn btn-primary"
              >
                {isSearching ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Searching...
                  </>
                ) : (
                  <>
                    <HiMagnifyingGlass className="w-5 h-5" />
                    Search
                  </>
                )}
              </button>
              {hasSearched && (
                <button
                  onClick={handleClearSearch}
                  className="btn btn-ghost"
                  title="Clear search"
                >
                  <HiXMark className="w-5 h-5" />
                </button>
              )}
            </div>
            <div className="text-sm text-base-content/60 mt-2">
              <HiUsers className="w-4 h-4 inline mr-1" />
              {hasSearched 
                ? `Found ${availableSearchResults.length} matching user${availableSearchResults.length !== 1 ? 's' : ''}`
                : `Browsing first 20 users - use search to find specific athletes`
              }
            </div>
          </div>

          {/* Available Users */}
          {(usersLoading && !hasSearched) || isSearching ? (
            <div className="flex justify-center items-center py-8">
              <div className="text-center">
                <div className="loading loading-spinner loading-lg mb-4"></div>
                <p className="text-base-content/70">{isSearching ? 'Searching...' : 'Loading users...'}</p>
              </div>
            </div>
          ) : hasSearched ? (
            availableSearchResults.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                No users found matching "{searchTerm}". Try a different search term.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr className="bg-base-200">
                      <th className="font-semibold">User</th>
                      <th className="font-semibold">Username</th>
                      <th className="font-semibold">UID</th>
                      <th className="font-semibold">Sport</th>
                      <th className="font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableSearchResults.map((user) => (
                      <tr key={user.userId} className="hover:bg-base-200">
                        <td>
                          <div className="flex items-center space-x-3">
                            {user.publicData?.imageUrlTiny || user.publicData?.imageUrl ? (
                              <img
                                src={user.publicData.imageUrlTiny || user.publicData.imageUrl}
                                alt={`${user.username}'s avatar`}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                                {user.username?.charAt(0).toUpperCase() || '?'}
                              </div>
                            )}
                            <div>
                              <div className="font-medium">
                                {user.publicData?.name || 'Unknown'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-outline">{user.username}</span>
                        </td>
                        <td>
                          <code className="text-xs bg-base-200 px-2 py-1 rounded">
                            {user.userId.slice(0, 8)}...
                          </code>
                        </td>
                        <td>
                          {getSportNames(user.publicData?.trainingSportIDs) ||
                            'N/A'}
                        </td>
                        <td>
                          <button
                            onClick={() => handleAddAthlete(user.userId)}
                            disabled={addingUserId === user.userId}
                            className="btn btn-sm btn-primary"
                            title="Add to suggested athletes"
                          >
                            {addingUserId === user.userId ? (
                              <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                              <>
                                <HiPlus className="w-4 h-4 mr-1" />
                                Add
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : availableUsers.length === 0 ? (
            <div className="text-center py-8 text-base-content/60">
              All users in the first 20 have been added to suggested athletes. Use search to find more users.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr className="bg-base-200">
                    <th className="font-semibold">User</th>
                    <th className="font-semibold">Username</th>
                    <th className="font-semibold">UID</th>
                    <th className="font-semibold">Sport</th>
                    <th className="font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {availableUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-base-200">
                      <td>
                        <div className="flex items-center space-x-3">
                          {user.userData.imageUrlTiny || user.userData.imageUrl ? (
                            <img
                              src={user.userData.imageUrlTiny || user.userData.imageUrl}
                              alt={`${user.userData.name}'s avatar`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                              {user.userData.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {user.userData.name} {user.userData.surname}
                            </div>
                            <div className="text-sm text-base-content/60">
                              {user.userData.username || 'No username'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {user.userData.username ? (
                          <span className="badge badge-outline">{user.userData.username}</span>
                        ) : (
                          <span className="text-base-content/50 text-sm">No username</span>
                        )}
                      </td>
                      <td>
                        <code className="text-xs bg-base-200 px-2 py-1 rounded">
                          {user.id.slice(0, 8)}...
                        </code>
                      </td>
                      <td>
                        {getSportNames(user.userData.trainingSportIDs) ||
                          'N/A'}
                      </td>
                      <td>
                        <button
                          onClick={() => handleAddAthlete(user.id)}
                          disabled={addingUserId === user.id}
                          className="btn btn-sm btn-primary"
                          title="Add to suggested athletes"
                        >
                          {addingUserId === user.id ? (
                            <span className="loading loading-spinner loading-sm"></span>
                          ) : (
                            <>
                              <HiPlus className="w-4 h-4 mr-1" />
                              Add
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuggestedAthletesManagement;

